import { getPublicConfig } from 'api/config'
import { useQuery } from 'react-query'
import { PublicConfigPlan, SubscriptionPlanType } from 'types'

const PUBLIC_CONFIG_KEY = 'publicConfig'

/**
 * Prices and billing periods served by the backend from Stripe. Cached for the whole session —
 * they change about as often as the price list does.
 */
export function usePublicConfig() {
  const { data, isLoading } = useQuery([PUBLIC_CONFIG_KEY], getPublicConfig, {
    staleTime: Infinity,
    retry: false,
  })

  return { config: data ?? null, isLoading }
}

/**
 * Plan details for a single plan, or `null` while loading or when the backend could not resolve
 * its Stripe price. Callers render their own fallback rather than a wrong price.
 */
export function usePlanConfig(type: SubscriptionPlanType): PublicConfigPlan | null {
  const { config } = usePublicConfig()
  return config?.plans?.[type] ?? null
}

/** "$12" / "€12.50" — no decimals for whole amounts, which is how the prices are set today. */
export function formatPlanPrice(plan: PublicConfigPlan): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: plan.currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(plan.price) ? 0 : 2,
  }).format(plan.price)
}

/** "month" / "year" / "3 months" — matches how the period reads inside a sentence. */
export function formatPlanPeriod(plan: PublicConfigPlan): string {
  return plan.intervalCount > 1 ? `${plan.intervalCount} ${plan.interval}s` : plan.interval
}
